import AES from 'crypto-js/aes';
import type { TFunction } from 'i18next';
import type { AppRouterInstance } from 'next/dist/shared/lib/app-router-context.shared-runtime';
import { assoc, curry, equals, findIndex, keys, pipe, reduce } from 'ramda';
import { SPORTS_ROUTES } from '@/modules/sports/helpers';
import { type RangeIP, REACT_APP_API_URL } from './conf';
import { CLIENT_ENVS } from './constants';

export function isObject(o: any) {
  return o === Object(o) && !Array.isArray(o) && typeof o !== 'function';
}

export function is(x: any, y: any) {
  if (x === y) {
    return x !== 0 || y !== 0 || 1 / x === 1 / y;
  }
  return x !== x && y !== y;
}

export function shallowEqual(objA: any, objB: any) {
  if (is(objA, objB)) return true;

  if (
    typeof objA !== 'object' ||
    objA === null ||
    typeof objB !== 'object' ||
    objB === null
  ) {
    return false;
  }

  const keysA = Object.keys(objA);
  const keysB = Object.keys(objB);

  if (keysA.length !== keysB.length) return false;

  const hasOwn = Object.prototype.hasOwnProperty;
  for (let i = 0; i < keysA.length; i++) {
    if (!(hasOwn.call(objB, keysA[i]) && is(objA[keysA[i]], objB[keysA[i]]))) {
      return false;
    }
  }

  return true;
}

export function toCamel(s: string) {
  if (!s.includes('_')) return s;

  return s.replace(/([_][a-z])/gi, ($1) => $1.toUpperCase().replace('_', ''));
}

export function toSnake(s: string) {
  return s.replace(/[\w]([A-Z])/g, (m) => m[0] + '_' + m[1]).toLowerCase();
}

export function camelToNormalize(s: string) {
  return s.replace(/[\w]([A-Z])/g, (m) => m[0] + ' ' + m[1]).toLowerCase();
}

export function deepKeysTransformFactory(transform: (key: string) => string) {
  function deepKeysTransform<T extends { [key: string]: any } | any[]>(
    o: T,
    blacklists?: string[][]
  ): object {
    if (isObject(o)) {
      return Object.keys(o).reduce(
        (previous, current) => {
          const transformedKey = transform(current);

          const keyValue = (o as { [key: string]: any })[current];

          if (blacklists) {
            const keyBlacklists = blacklists
              .filter((bl) => bl[0] === transformedKey)
              .map((bl) => bl.slice(1));

            if (keyBlacklists.find((bl) => bl.length === 0)) {
              previous[transformedKey] = keyValue;
            } else {
              previous[transformedKey] = deepKeysTransform(
                keyValue,
                keyBlacklists
              );
            }
          } else {
            previous[transformedKey] = deepKeysTransform(keyValue);
          }

          return previous;
        },
        {} as { [key: string]: any }
      );
    }
    if (Array.isArray(o)) {
      return o.map((i) => deepKeysTransform(i, blacklists));
    }

    return o;
  }

  return deepKeysTransform;
}

export const camelize = deepKeysTransformFactory(toCamel);
export const snakify = deepKeysTransformFactory(toSnake);

// date: MM/DD/YYYY
export function dateFormat(date: string) {
  const regEx = /^(\d{2})\/(\d{2})\/(\d{4})$/;
  const matchResult = date.match(regEx);

  if (
    matchResult != null &&
    matchResult[1] != null &&
    matchResult[2] != null &&
    matchResult[3] != null
  ) {
    return `${matchResult[3]}-${matchResult[2]}-${matchResult[1]}`;
  }

  return date;
}

export function removeSpaces(str: string) {
  return str.replace(/\s/g, '');
}

export function uuid() {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0,
      v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}
export const getSrc = (src: string) => `${REACT_APP_API_URL}${src}`;

export function delay(ms: number) {
  return new Promise((res) => {
    setTimeout(res, ms);
  });
}

export function entries<T extends { [key: string]: any }>(
  obj: T
): [string, T[keyof T]][] {
  const ownProps = Object.keys(obj);
  let i = ownProps.length;
  const resArray = new Array(i); // preallocate the Array
  while (i--) resArray[i] = [ownProps[i], obj[ownProps[i]]];

  return resArray;
}

export function parseFiltersFromQS<T extends string>(
  querystring: string,
  filters: T[]
): Partial<{ [K in T]: string | undefined }> {
  const searchParams = new URLSearchParams(querystring);
  const parsedFilters: { [key: string]: any } = {};

  const entriesArray = Array.from(searchParams.entries());

  for (const [filter, value] of entriesArray) {
    if (filters.includes(filter as T)) {
      parsedFilters[filter] = value;
    }
  }

  return parsedFilters as Partial<{ [K in T]: string | undefined }>;
}

export function capitalize(s: string) {
  return s[0].toUpperCase() + s.slice(1);
}

export const capitalizeWords = (s: string) =>
  s.toLowerCase().replace(/(^|[\s-])\S/g, (char) => char.toUpperCase());

export function formatDate(dateStr: string, t: TFunction, mouthMapKey: string) {
  const date = new Date(dateStr);
  const year = date.getFullYear();
  const monthIso = date.getMonth();
  const dayIso = date.getDate();
  const day = dayIso < 9 ? `0${dayIso}` : dayIso;
  const month = capitalize(t(`${mouthMapKey}.${monthIso}`));

  return `${day} ${month} ${year}`;
}

function updateHistory(replaceUrl: boolean, url: string) {
  const fn = replaceUrl
    ? window.history.replaceState
    : window.history.pushState;
  fn.call(window.history, {}, document.title, url);
}

function addQueryParameterPure(url: string, key: string, value: string) {
  const urlObject = new URL(url);
  urlObject.searchParams.set(key, value);
  return urlObject.href;
}

export function addUrlParameter(
  key: string,
  value: string,
  replaceUrl?: boolean
) {
  // add URL parameters without refreshing page
  if (typeof window === 'undefined') return;

  const currentUrl = window.location.href;

  const newUrl = addQueryParameterPure(currentUrl, key, value);

  if (currentUrl === newUrl) return;

  updateHistory(!!replaceUrl, newUrl);
}

function removeQueryParameterPure(url: string, key: string) {
  const urlObject = new URL(url);
  urlObject.searchParams.delete(key);
  return urlObject.href;
}

export function removeUrlParameters(keys: string[], replaceUrl?: boolean) {
  // remove URL parameters without refreshing page
  if (typeof window === 'undefined') return;

  const currentUrl = window.location.href;

  const newUrl = keys.reduce(
    (acc, key) => removeQueryParameterPure(acc, key),
    currentUrl
  );

  if (currentUrl === newUrl) return;

  updateHistory(!!replaceUrl, newUrl);
}

export const toSlug = (input: string) =>
  input.trim().replace(/-/g, '~').replace(/\s+/g, '-').toLowerCase();

// case insensentive
export const fromSlug = (encodedSlug: string) => {
  const decodedSlug = decodeURIComponent(encodedSlug);
  return decodedSlug.replace(/-/g, ' ').replace(/~/g, '-');
};

export function timestamp(time?: string) {
  return time ? new Date(time).getTime() : new Date().getTime();
}

export const revertBoolean = (value: boolean) => !value;

export const findBoolean = <T>(fn: (a: T) => boolean) =>
  pipe(findIndex(fn), equals(-1), revertBoolean);

export const removeAllSpecialCharactersFromString = (str: string) =>
  str.replace(/[^a-zA-Z]/g, '');

export const createScript = (url: string, isDefer?: boolean) => {
  const script = document.createElement('script');

  script.src = url;
  script.type = 'text/javascript';
  script.id = removeAllSpecialCharactersFromString(url);

  if (isDefer) {
    script.defer = true;
  } else {
    script.async = true;
  }

  document.head.appendChild(script);
};

export const deleteScript = (url: string) => {
  const id = removeAllSpecialCharactersFromString(url);
  const element = document.getElementById(id);

  if (element == null) return;

  element.remove();
};

export const getRandomItemFromArray = <T>(array: T[]): T =>
  array[Math.floor(Math.random() * array.length)];

export const isSvg = (url: string) => /\.(svg)$/.test(url);

export function setCookie(name: string, value: string, days?: number) {
  if (typeof document === 'undefined') return;

  let expires = '';
  if (days) {
    const date = new Date();
    date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
    expires = '; expires=' + date.toUTCString();
  }
  document.cookie = name + '=' + (value || '') + expires + '; path=/';
}

export function getCookie(name: string) {
  if (typeof document === 'undefined') return;

  const nameEQ = name + '=';
  const ca = document.cookie.split(';');
  for (let i = 0; i < ca.length; i++) {
    let c = ca[i];
    while (c.charAt(0) === ' ') c = c.substring(1, c.length);
    if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
  }
  return null;
}

export enum CookieType {
  String = 0,
  Object = 1,
}
export interface FactoryCookieProps {
  name: string;
  value:
    | {
        type: CookieType.String;
        data: string;
      }
    | {
        type: CookieType.Object;
        data: object | undefined;
      };
  days: number;
  withReplace?: boolean;
  encryptKey?: string;
}

export function factoryCookie({
  name,
  value,
  days,
  withReplace,
  encryptKey,
}: FactoryCookieProps) {
  const data = (() => {
    switch (value.type) {
      case CookieType.String:
        return value.data;

      case CookieType.Object:
        if (value.data == null) return '';
        return JSON.stringify(value.data);

      default:
        return '';
    }
  })();

  if (data === '') return;

  const transData = encryptKey
    ? AES.encrypt(data, encryptKey).toString()
    : data;

  if (withReplace) {
    setCookie(name, transData, days);
    return;
  }

  const valueCookie = getCookie(name);
  if (valueCookie != null) return;
  setCookie(name, transData, days);
}

export function eraseCookie(name: string) {
  document.cookie = name + '=; Path=/; Expires=Thu, 01 Jan 1970 00:00:01 GMT;';
}

export const isExternalURL = (url: string) => {
  try {
    return new URL(url)?.origin !== window.location.origin;
  } catch {
    return false;
  }
};

export const renameKeys = curry((keysMap, obj) =>
  reduce((acc, key) => assoc(keysMap[key] || key, obj[key], acc), {}, keys(obj))
);

export const mergeRefs =
  (...refs: any[]) =>
  (node: any) => {
    for (const ref of refs) {
      ref.current = node;
    }
  };

export const getRandomElementFromList = <T>(list: T[]): T =>
  list[Math.floor(Math.random() * list.length)];

export const randomFromIntervalCrypto = (min: number, max: number) =>
  Math.random() * (max - min) + min;

export const randomIntFromInterval = (min: number, max: number) =>
  Math.floor(Math.random() * (max - min + 1)) + min;

interface ProbabilityOption {
  chanceFrom: number;
  chanceTo: number;
  option: any;
}

export const getResultWithProbability = (options: ProbabilityOption[]) => {
  const randomInt = Math.random() * 100;

  for (const { chanceFrom, chanceTo, option } of options) {
    if (randomInt >= chanceFrom && randomInt <= chanceTo) {
      return option;
    }
  }
};

const ipToNumber = (ip: string) =>
  +ip
    .split('.')
    .map((d) => ('000' + d).substr(-3))
    .join('');

const isIPb6 = (ip: string) => ip.includes(':');

export const checkIpInRange = (blackList: RangeIP[]) => (ip: string) =>
  isIPb6(ip)
    ? false
    : blackList.some(
        ([min, max]) =>
          ipToNumber(min) <= ipToNumber(ip) && ipToNumber(ip) <= ipToNumber(max)
      );

export const getValuesFromLocalStorage = (storageName: string) => {
  const valuesStr =
    typeof localStorage !== 'undefined'
      ? localStorage.getItem(storageName)
      : null;

  if (!valuesStr) return;

  return isValidJSON(valuesStr) ? JSON.parse(valuesStr) : valuesStr;
};

export const createItems = <T>(item: T, count: number) =>
  Array.apply(null, Array(count)).map(() => item);

export const groupItems = (items: any[], rows: number, cols = 1) => {
  const chunkSize = rows * cols;

  return Array.from(
    { length: Math.ceil(items.length / chunkSize) },
    (_, i) => ({
      itemsGroup: items.slice(i * chunkSize, i * chunkSize + chunkSize),
      rows,
      cols,
    })
  );
};

export function hasHtmlContent(html: string): boolean {
  const regexText = />([^<]+)</;

  return regexText.test(html);
}

export const isEmpty = (value: any) => value == null || value === '';

export const notEmpty = (value: any) => !isEmpty(value);

export const checkRichTextContent = (text: string | null | undefined) => {
  if (!text) return false;

  const tagRegex = /<[^>]*>/gm;
  const contentRegex = /[^<>\s]+/gm;

  return contentRegex.test(text?.replace(tagRegex, ''));
};

export const nickEncrypting = (nick: string) => {
  const stars = '***';
  const chartCount = nick.length >= 5 ? 3 : 1;

  return nick.slice(0, chartCount) + stars;
};

export const removeQueryParameter = (
  keys: string | string[],
  push: AppRouterInstance['push']
) => {
  if (typeof window === 'undefined') return;

  const searchParams = new URLSearchParams(window.location.search);
  const keysArray = typeof keys === 'string' ? [keys] : keys;

  keysArray.forEach((key) => searchParams.delete(key));

  push(
    `${window.location.pathname}?${decodeURIComponent(searchParams.toString())}`
  );
};

export const addQueryParameters = (
  push: AppRouterInstance['push'],
  parameters: Record<string, string>
) => {
  if (typeof window === 'undefined') return;

  const searchParams = new URLSearchParams(window.location.search);

  Object.entries(parameters).forEach(([key, value]) => {
    searchParams.set(key, value);
  });

  push(
    `${window.location.pathname}?${decodeURIComponent(searchParams.toString())}`
  );
};

export const isIos = () => {
  if (typeof window === 'undefined') return false;

  return (
    [
      'iPad Simulator',
      'iPhone Simulator',
      'iPod Simulator',
      'iPad',
      'iPhone',
      'iPod',
    ].includes(navigator.platform) ||
    (navigator.userAgent.includes('Mac') && 'ontouchend' in document)
  );
};

export const checkIsIOSChrome = () => {
  if (typeof window === 'undefined') return false;

  return isIos() && /CriOS/i.test(navigator.userAgent);
};

export const isProduction = () => CLIENT_ENVS.APP_ENV === 'production';

export function modifyKebabToCapitalize(str: string) {
  return str
    .split('-')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

export function kebabToCamel(s: string) {
  if (!s.includes('-')) return s;

  return s.replace(/([-][a-z])/gi, ($1) => $1.toUpperCase().replace('-', ''));
}

export const isValidJSON = (str: string) => {
  try {
    JSON.parse(str);
    return true;
  } catch (err) {
    console.error(err);
  }
};

export const generateLink = (link: string, locale: string) =>
  locale === 'en' ? link : `/${locale}${link === '/' ? '' : link}`;

export const getPathnameFromHeader = (
  pathnameHeader: string | null,
  locale: string
) =>
  !pathnameHeader || pathnameHeader === '/'
    ? ''
    : pathnameHeader.replace(new RegExp(`^/${locale}`), '');

export const debounce = <T extends (...args: any[]) => void>(
  func: T,
  wait: number
) => {
  let timeout: NodeJS.Timeout;

  return (...args: Parameters<T>) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
};

export const formatTextUppercase = (code: string) => code.trim().toUpperCase();

export type TAppSectionType = 'casino' | 'sports';

export const getCurrentAppSection = (): TAppSectionType => {
  const { pathname } = window.location;

  const isSports = pathname.includes(SPORTS_ROUTES);
  if (isSports) return 'sports';

  return 'casino';
};

export const removeNonAlphanumeric = (str: string) =>
  str.replace(/[^a-zA-Z0-9]/g, '');
